import { useEffect, useState } from "react";
import { ABI, Serializer } from "@wharfkit/antelope";
import { useWallet } from "../wallet/WalletContext";
import { chain, contracts } from "../config";

// One-time installer for escrowbacker v2 (fixes the logmint handler that made
// every backed mint revert — see deploy-escrowbacker-v2/README-v2.md). Only
// usable when the connected wallet IS the escrow contract account, since
// setcode/setabi must be signed by that account. The wallet signs; this page
// never sees a key. eosio.code is already linked on the account, so no
// permission change is included.
const WASM_URL = "/escrowbacker-v2/escrowbacker.wasm";
const ABI_URL = "/escrowbacker-v2/escrowbacker.abi";
const V2_HASH = "38a79dc0b38e3e063f4832ffbad0c1983718fc8c1803916bfdd90525512b2a59";

const toHex = (buf: ArrayBuffer) => [...new Uint8Array(buf)].map((b) => b.toString(16).padStart(2, "0")).join("");

async function onChainCodeHash(): Promise<string> {
  const res = await fetch(`${chain.url}/v1/chain/get_code_hash`, {
    method: "POST",
    body: JSON.stringify({ account_name: contracts.escrow }),
  });
  const j = await res.json();
  return j.code_hash as string;
}

export function EscrowUpgradePanel() {
  const { session, account } = useWallet();
  const [hash, setHash] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<string | null>(null);
  const [err, setErr] = useState<string | null>(null);

  const refresh = () => onChainCodeHash().then((h) => setHash(h ?? null)).catch(() => setHash(null));
  useEffect(() => { refresh(); }, []);

  const installed = hash === V2_HASH;
  const canSign = account === contracts.escrow && !!session;

  async function install() {
    if (!session) return;
    setBusy(true);
    setErr(null);
    setMsg(null);
    try {
      const [wasmBuf, abiJson] = await Promise.all([
        fetch(WASM_URL).then((r) => r.arrayBuffer()),
        fetch(ABI_URL).then((r) => r.json()),
      ]);
      const digest = toHex(await crypto.subtle.digest("SHA-256", wasmBuf));
      if (digest !== V2_HASH) throw new Error(`Downloaded contract doesn't match the verified build (got ${digest.slice(0, 12)}…). Not installing.`);
      const abiHex = Serializer.encode({ object: ABI.from(abiJson) }).hexString;
      const auth = [{ actor: contracts.escrow, permission: "active" }];
      const result = await session.transact({
        actions: [
          { account: "eosio", name: "setcode", authorization: auth, data: { account: contracts.escrow, vmtype: 0, vmversion: 0, code: toHex(wasmBuf) } },
          { account: "eosio", name: "setabi", authorization: auth, data: { account: contracts.escrow, abi: abiHex } },
        ],
      } as any);
      const txid = String((result as any)?.resolved?.transaction?.id ?? (result as any)?.response?.transaction_id ?? "");
      setMsg(`Installed. Transaction ${txid.slice(0, 16)}…`);
      [2000, 5000, 10000].forEach((ms) => setTimeout(refresh, ms));
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="admin-section">
      <h2>Escrow contract update (v2)</h2>
      <p className="admin-section__subtitle">
        v2 fixes the bug that made every backed mint fail. Same tables, actions, and rules — only the mint/burn
        notification handlers changed. Verified build sha256 <code>{V2_HASH.slice(0, 12)}…</code>.
      </p>
      <p>
        <code>{contracts.escrow}</code> is running:{" "}
        <strong>{hash === null ? "checking…" : installed ? "v2 ✓ (up to date)" : `old version (${hash.slice(0, 12)}…)`}</strong>
      </p>
      {!installed && (
        canSign ? (
          <button className="btn btn--primary" onClick={install} disabled={busy}>
            {busy ? "Waiting for wallet…" : "Install escrow update"}
          </button>
        ) : (
          <div className="callout callout--warning">Connect as <code>{contracts.escrow}</code> to install the update.</div>
        )
      )}
      {msg && <div className="state-message">{msg}</div>}
      {err && <div className="state-message state-message--error">{err}</div>}
    </div>
  );
}
